import { useState } from 'react';
import { FileSpreadsheet, FileText, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api from '../lib/api';

/**
 * Download file dari endpoint export (blob) dengan nama dari Content-Disposition.
 * Error dari server tetap JSON meski responseType blob — dibaca lalu ditampilkan.
 */
export async function downloadExport(url, params, fallbackName) {
  try {
    const res = await api.get(url, { params, responseType: 'blob' });
    const cd = res.headers['content-disposition'] || '';
    const match = cd.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : fallbackName;
    const href = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(href);
    toast.success(`File ${filename} berhasil diunduh`);
    return true;
  } catch (error) {
    let message = 'Gagal mengunduh export';
    const data = error.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (typeof parsed.detail === 'string') message = parsed.detail;
      } catch (e) { /* bukan JSON */ }
    }
    toast.error(message);
    return false;
  }
}

/**
 * Tombol "Export" dengan pilihan Excel / CSV untuk satu tabel.
 * `params` = filter aktif di layar (search, status, sort, bank_type, ...),
 * sehingga isi file sama dengan tabel yang sedang dilihat (semua halaman).
 */
export function ExportMenu({ table, params = {}, label = 'Export', size = 'sm', className = '', testId }) {
  const [busy, setBusy] = useState(false);
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'all'));
  const run = async (format) => {
    setBusy(true);
    await downloadExport(`/export/${table}`, { ...clean, format }, `${table}.${format}`);
    setBusy(false);
  };
  const tid = testId || `export-${table}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size={size} variant="outline" disabled={busy} className={className} data-testid={tid}>
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => run('xlsx')} data-testid={`${tid}-xlsx`}>
          <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => run('csv')} data-testid={`${tid}-csv`}>
          <FileText className="h-4 w-4 mr-2 text-slate-600" /> CSV (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
